import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon, { type IconName } from '../../../components/AppIcon';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Pagination from '@/components/Pagination';
import { LoadingState } from '../../../components/AsyncStates';
import { useMyAssignments } from '@/hooks/useMyAssignments';
import { useMyWorkPoints } from '@/hooks/useMyWorkPoints';
import { acceptAssignment, declineAssignment } from '@/api/TournamentManagement/refereeApi';
import { useToast, ToastRenderer } from '@/hooks/useToast';
import { MatchStatus, MatchRole, AssignmentResponse, resolveEnum } from '@/models/tournament';
import { useCurrency } from '@/hooks/useCurrency';
import type { MyAssignmentItem } from '@/models/tournament';
import DeclineAssignmentModal from './components/DeclineAssignmentModal';
import MyScheduleTab from './components/MyScheduleTab';
import WorkPointsTab from './components/WorkPointsTab';

type HubTab = 'assignments' | 'schedule' | 'workpoints';

const MyAssignments: React.FC = () => {
  const { t, i18n } = useTranslation('tournament');
  const { format: formatCurrency } = useCurrency();
  const [hubTab, setHubTab] = useState<HubTab>('assignments');
  const [assignmentTab, setAssignmentTab] = useState<'upcoming' | 'past'>('upcoming');

  // Get current month date range
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

  const { data: workPointsSummary, loading: wpLoading } = useMyWorkPoints({
    startDate: startOfMonth,
    endDate: endOfMonth,
  });
  const { data: assignments, loading: assignmentsLoading, refetch } = useMyAssignments({ pageSize: 100 });

  const { toast, showToast, dismissToast } = useToast();
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineMatchId, setDeclineMatchId] = useState<number>(0);
  const [declineMatchDescription, setDeclineMatchDescription] = useState('');
  const [respondingMatchId, setRespondingMatchId] = useState<number | null>(null);

  const statusKeys: Record<MatchStatus, string> = {
    [MatchStatus.Scheduled]: 'Scheduled',
    [MatchStatus.InProgress]: 'In Progress',
    [MatchStatus.Completed]: 'Completed',
    [MatchStatus.Closed]: 'Closed',
    [MatchStatus.UnderRevision]: 'Under Revision',
    [MatchStatus.Cancelled]: 'Cancelled',
  };

  const roleKeys: Record<MatchRole, string> = {
    [MatchRole.HeadReferee]: 'Head Referee',
    [MatchRole.AssistantReferee]: 'Assistant Referee',
    [MatchRole.FourthOfficial]: 'Fourth Official',
    [MatchRole.Commissioner]: 'Commissioner',
  };

  const getStatusColor = (status: MatchStatus): string => {
    switch (status) {
      case MatchStatus.Scheduled:
        return 'bg-blue-100 text-blue-800';
      case MatchStatus.InProgress:
        return 'bg-yellow-100 text-yellow-800';
      case MatchStatus.Completed:
        return 'bg-green-100 text-green-800';
      case MatchStatus.Closed:
        return 'bg-gray-100 text-gray-800';
      case MatchStatus.UnderRevision:
        return 'bg-purple-100 text-purple-800';
      case MatchStatus.Cancelled:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });
  };

  const handleAccept = async (matchId: number) => {
    setRespondingMatchId(matchId);
    try {
      const result = await acceptAssignment({ matchId });
      if (result.errors.length) {
        showToast(result.errors[0], 'error');
      } else {
        showToast(t('Assignment accepted successfully'), 'success');
        refetch();
      }
    } catch {
      showToast(t('Failed to accept assignment'), 'error');
    } finally {
      setRespondingMatchId(null);
    }
  };

  const handleOpenDeclineModal = (assignment: MyAssignmentItem) => {
    setDeclineMatchId(assignment.matchId);
    setDeclineMatchDescription(`${assignment.team1Name} vs ${assignment.team2Name}`);
    setShowDeclineModal(true);
  };

  const handleDecline = async (matchId: number, reason?: string) => {
    const result = await declineAssignment({ matchId, reason });
    if (result.errors.length) {
      showToast(result.errors[0], 'error');
      throw new Error(result.errors[0]);
    }
    showToast(t('Assignment declined'), 'success');
    refetch();
  };

  // Filters for upcoming + past lists
  const [searchFilter, setSearchFilter] = useState('');
  const [tournamentFilter, setTournamentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [responseFilter, setResponseFilter] = useState('');

  // Pagination state — independent for upcoming and past
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [pastPage, setPastPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const applyFilters = (list: MyAssignmentItem[]) => {
    let out = list;
    if (searchFilter.trim()) {
      const q = searchFilter.trim().toLowerCase();
      out = out.filter((a) =>
        (a.team1Name ?? '').toLowerCase().includes(q) ||
        (a.team2Name ?? '').toLowerCase().includes(q) ||
        (a.location ?? '').toLowerCase().includes(q) ||
        (a.tournamentName ?? '').toLowerCase().includes(q)
      );
    }
    if (tournamentFilter) out = out.filter((a) => String(a.tournamentId ?? '') === tournamentFilter);
    if (statusFilter !== '') {
      const want = parseInt(statusFilter);
      out = out.filter((a) => {
        const s = (resolveEnum(MatchStatus, a.matchStatus) as MatchStatus) ?? MatchStatus.Scheduled;
        return s === want;
      });
    }
    if (roleFilter !== '') {
      const want = parseInt(roleFilter);
      out = out.filter((a) => {
        const r = (resolveEnum(MatchRole, a.role) as MatchRole) ?? MatchRole.HeadReferee;
        return r === want;
      });
    }
    if (responseFilter !== '') {
      const want = parseInt(responseFilter);
      out = out.filter((a) => {
        const r = (resolveEnum(AssignmentResponse, a.responseStatus) as AssignmentResponse) ?? AssignmentResponse.Pending;
        return r === want;
      });
    }
    return out;
  };

  const upcomingAssignments = useMemo(() => {
    if (!assignments?.items) return [] as MyAssignmentItem[];
    const base = assignments.items.filter((a) => {
      const s = (resolveEnum(MatchStatus, a.matchStatus) as MatchStatus) ?? MatchStatus.Scheduled;
      return s === MatchStatus.Scheduled || s === MatchStatus.InProgress || s === MatchStatus.UnderRevision;
    });
    return applyFilters(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments, searchFilter, tournamentFilter, statusFilter, roleFilter, responseFilter]);

  const pastAssignments = useMemo(() => {
    if (!assignments?.items) return [] as MyAssignmentItem[];
    const base = assignments.items.filter((a) => {
      const s = (resolveEnum(MatchStatus, a.matchStatus) as MatchStatus) ?? MatchStatus.Scheduled;
      return s === MatchStatus.Completed || s === MatchStatus.Closed || s === MatchStatus.Cancelled;
    });
    return applyFilters(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments, searchFilter, tournamentFilter, statusFilter, roleFilter, responseFilter]);

  // Reset paging when filters or tab change
  useEffect(() => { setUpcomingPage(1); setPastPage(1); }, [searchFilter, tournamentFilter, statusFilter, roleFilter, responseFilter]);

  // Unique tournaments from my assignments for the tournament filter dropdown
  const myTournamentOptions = useMemo(() => {
    const items = assignments?.items ?? [];
    const seen = new Map<string, string>();
    items.forEach((a) => {
      const key = String(a.tournamentId ?? '');
      if (key && !seen.has(key)) seen.set(key, a.tournamentName ?? key);
    });
    return [{ value: '', label: t('All Tournaments') }, ...Array.from(seen.entries()).map(([value, label]) => ({ value, label }))];
  }, [assignments, t]);

  const paginate = <T,>(list: T[], page: number): { slice: T[]; totalPages: number } => {
    const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
    const start = (page - 1) * pageSize;
    return { slice: list.slice(start, start + pageSize), totalPages };
  };
  const upcomingPaged = paginate(upcomingAssignments, upcomingPage);
  const pastPaged = paginate(pastAssignments, pastPage);

  const clearFilters = () => {
    setSearchFilter('');
    setTournamentFilter('');
    setStatusFilter('');
    setRoleFilter('');
    setResponseFilter('');
  };
  const hasActiveFilters = !!(searchFilter || tournamentFilter || statusFilter || roleFilter || responseFilter);

  const loading = wpLoading || assignmentsLoading;

  const renderAssignmentCard = (assignment: MyAssignmentItem, showPoints: boolean) => (
    <div
      key={assignment.matchId}
      className="bg-card border border-border rounded-lg p-4 hover:shadow-sm transition-shadow"
      data-testid={`assignment-card-${assignment.matchId}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <p className="text-xs text-gray-500 mb-1">{assignment.tournamentName}</p>
          <p className="text-sm font-semibold text-gray-900">
            {assignment.team1Name} <span className="text-gray-400">{t('vs')}</span> {assignment.team2Name}
          </p>
        </div>
        <div className="flex items-center space-x-2 rtl:space-x-reverse">
          {showPoints && assignment.workPoints > 0 && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-testid={`work-points-badge-${assignment.matchId}`}>
              <Icon name="Zap" size={12} className="me-1" />
              {assignment.workPoints} {t('pts')}
            </span>
          )}
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor((resolveEnum(MatchStatus, assignment.matchStatus) as MatchStatus) ?? MatchStatus.Scheduled)}`}>
            {t(statusKeys[(resolveEnum(MatchStatus, assignment.matchStatus) as MatchStatus) ?? MatchStatus.Scheduled] ?? 'Unknown')}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-gray-600">
        <div className="flex items-center">
          <Icon name="Calendar" size={12} className="me-1.5 text-gray-400" />
          <span>{formatDate(assignment.scheduledDateTime)}</span>
        </div>
        <div className="flex items-center">
          <Icon name="Clock" size={12} className="me-1.5 text-gray-400" />
          <span>{formatTime(assignment.scheduledDateTime)}</span>
        </div>
        <div className="flex items-center">
          <Icon name="MapPin" size={12} className="me-1.5 text-gray-400" />
          <span className="truncate">{assignment.location}</span>
        </div>
      </div>

      <div className="mt-2 flex items-center text-xs">
        <Icon name="UserCog" size={12} className="me-1.5 text-primary" />
        <span className="font-medium text-primary">{t(roleKeys[(resolveEnum(MatchRole, assignment.role) as MatchRole) ?? MatchRole.HeadReferee] ?? 'Unknown')}</span>
      </div>

      {/* Accept/Decline buttons or status badge */}
      {resolveEnum(AssignmentResponse, assignment.responseStatus) === AssignmentResponse.Pending && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            onClick={() => handleAccept(assignment.matchId)}
            loading={respondingMatchId === assignment.matchId}
            disabled={respondingMatchId !== null}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid={`accept-btn-${assignment.matchId}`}
          >
            <Icon name="CheckCircle" size={14} className="me-1.5" />
            {t('Accept')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleOpenDeclineModal(assignment)}
            disabled={respondingMatchId !== null}
            className="border-red-300 text-red-600 hover:bg-red-50"
            data-testid={`decline-btn-${assignment.matchId}`}
          >
            <Icon name="XCircle" size={14} className="me-1.5" />
            {t('Decline')}
          </Button>
          {assignment.responseDeadline && (
            <span className="text-xs text-gray-500 ms-auto">
              <Icon name="Clock" size={12} className="inline me-1" />
              {t('Respond by')} {formatDate(assignment.responseDeadline)}
            </span>
          )}
        </div>
      )}
      {resolveEnum(AssignmentResponse, assignment.responseStatus) === AssignmentResponse.Accepted && (
        <div className="mt-3">
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
            data-testid={`accepted-badge-${assignment.matchId}`}
          >
            <Icon name="CheckCircle" size={14} className="me-1" />
            {t('Accepted')}
          </span>
        </div>
      )}
      {resolveEnum(AssignmentResponse, assignment.responseStatus) === AssignmentResponse.Declined && (
        <div className="mt-3">
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
            data-testid={`declined-badge-${assignment.matchId}`}
          >
            <Icon name="XCircle" size={14} className="me-1" />
            {t('Declined')}
          </span>
        </div>
      )}
    </div>
  );

  const hubTabs: { id: HubTab; label: string; icon: string }[] = [
    { id: 'assignments', label: t('Assignments'), icon: 'ClipboardList' },
    { id: 'schedule', label: t('My Schedule'), icon: 'CalendarDays' },
    { id: 'workpoints', label: t('Work Points'), icon: 'Zap' },
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="my-assignments-page">
      {/* Page Header */}
      <div className="flex items-center space-x-3 rtl:space-x-reverse">
        <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-lg flex items-center justify-center shadow-sm">
          <Icon name="ClipboardList" size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('My Assignments')}</h1>
          <p className="text-sm text-gray-600">{t('View your referee assignments, schedule, and work points')}</p>
        </div>
      </div>

      {/* Summary Cards — shown only on Assignments tab */}
      {hubTab === 'assignments' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="summary-cards">
          {/* Total Work Points */}
          <div className="bg-card border border-border rounded-lg p-5" data-testid="card-total-work-points">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Icon name="Zap" size={20} className="text-blue-600" />
              </div>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{t('This Month')}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{workPointsSummary?.totalWorkPoints ?? 0}</p>
            <p className="text-sm text-gray-600">{t('Total Work Points')}</p>
          </div>

          {/* Currency Equivalent */}
          <div className="bg-card border border-border rounded-lg p-5" data-testid="card-currency-equivalent">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Icon name="DollarSign" size={20} className="text-green-600" />
              </div>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{t('This Month')}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(workPointsSummary?.totalCurrencyAmount ?? 0)}</p>
            <p className="text-sm text-gray-600">{t('Currency Equivalent')}</p>
          </div>

          {/* Match Count */}
          <div className="bg-card border border-border rounded-lg p-5" data-testid="card-match-count">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Icon name="Trophy" size={20} className="text-amber-600" />
              </div>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{t('This Month')}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{workPointsSummary?.matchCount ?? 0}</p>
            <p className="text-sm text-gray-600">{t('Matches This Month')}</p>
          </div>
        </div>
      )}

      {/* Hub Tab Navigation */}
      <div className="flex border-b border-border overflow-x-auto overflow-y-hidden" data-testid="hub-tabs">
        {hubTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setHubTab(tab.id)}
            className={`px-5 py-3 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap flex items-center gap-2 ${
              hubTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid={`hub-tab-${tab.id}`}
          >
            <Icon name={tab.icon as IconName} size={16} className="flex-shrink-0" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {hubTab === 'assignments' && (
        <div data-testid="hub-content-assignments">
          {loading ? (
            <div className="p-6">
              <LoadingState title={t('Loading data...')} />
            </div>
          ) : (
            <>
              {/* Assignment Sub-Tabs */}
              <div className="flex border-b border-border mb-4" data-testid="assignment-tabs">
                <button
                  onClick={() => setAssignmentTab('upcoming')}
                  className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    assignmentTab === 'upcoming'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  data-testid="tab-upcoming"
                >
                  <Icon name="Calendar" size={16} className="inline-block me-2 align-text-bottom" />
                  {t('Upcoming Assignments')} ({upcomingAssignments.length})
                </button>
                <button
                  onClick={() => setAssignmentTab('past')}
                  className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    assignmentTab === 'past'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  data-testid="tab-past"
                >
                  <Icon name="CheckCircle" size={16} className="inline-block me-2 align-text-bottom" />
                  {t('Past Assignments')} ({pastAssignments.length})
                </button>
              </div>

              {/* Filters */}
              <div className="bg-card rounded-lg border border-border p-4 mb-4" data-testid="my-assignments-filters">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                  <div className="lg:col-span-2">
                    <Input
                      type="text"
                      placeholder={t('Search teams, location, tournament...')}
                      value={searchFilter}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchFilter(e.target.value)}
                      icon="Search"
                      data-testid="my-assignments-search"
                    />
                  </div>
                  <Select
                    value={tournamentFilter}
                    onChange={(v: string) => setTournamentFilter(v)}
                    placeholder={t('All Tournaments')}
                    options={myTournamentOptions}
                    data-testid="my-assignments-tournament-filter"
                  />
                  <Select
                    value={statusFilter}
                    onChange={(v: string) => setStatusFilter(v)}
                    placeholder={t('All Statuses')}
                    options={[
                      { value: '', label: t('All Statuses') },
                      { value: '0', label: t('Scheduled') },
                      { value: '1', label: t('In Progress') },
                      { value: '2', label: t('Completed') },
                      { value: '3', label: t('Closed') },
                      { value: '4', label: t('Under Revision') },
                      { value: '5', label: t('Cancelled') },
                    ]}
                    data-testid="my-assignments-status-filter"
                  />
                  <Select
                    value={roleFilter}
                    onChange={(v: string) => setRoleFilter(v)}
                    placeholder={t('All Roles')}
                    options={[
                      { value: '', label: t('All Roles') },
                      { value: String(MatchRole.HeadReferee), label: t('Head Referee') },
                      { value: String(MatchRole.AssistantReferee), label: t('Assistant Referee') },
                      { value: String(MatchRole.FourthOfficial), label: t('Fourth Official') },
                      { value: String(MatchRole.Commissioner), label: t('Commissioner') },
                    ]}
                    data-testid="my-assignments-role-filter"
                  />
                  <Select
                    value={responseFilter}
                    onChange={(v: string) => setResponseFilter(v)}
                    placeholder={t('All Responses')}
                    options={[
                      { value: '', label: t('All Responses') },
                      { value: String(AssignmentResponse.Pending), label: t('Pending') },
                      { value: String(AssignmentResponse.Accepted), label: t('Accepted') },
                      { value: String(AssignmentResponse.Declined), label: t('Declined') },
                    ]}
                    data-testid="my-assignments-response-filter"
                  />
                </div>
                {hasActiveFilters && (
                  <div className="mt-3 flex items-center justify-end">
                    <button
                      onClick={clearFilters}
                      className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
                      data-testid="my-assignments-clear-filters"
                    >
                      <Icon name="X" size={14} />
                      {t('Clear Filters')}
                    </button>
                  </div>
                )}
              </div>

              {/* Assignment List */}
              <div className="space-y-3" data-testid={`assignment-list-${assignmentTab}`}>
                {assignmentTab === 'upcoming' ? (
                  upcomingAssignments.length > 0 ? (
                    <>
                      {upcomingPaged.slice.map((a) => renderAssignmentCard(a, false))}
                      {upcomingAssignments.length > pageSize && (
                        <div className="pt-2">
                          <Pagination
                            currentPage={upcomingPage}
                            totalPages={upcomingPaged.totalPages}
                            onPageChange={setUpcomingPage}
                            pageSize={pageSize}
                            onPageSizeChange={(s) => { setPageSize(s); setUpcomingPage(1); setPastPage(1); }}
                            totalCount={upcomingAssignments.length}
                            variant="full"
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="bg-card border border-border rounded-lg p-12 text-center" data-testid="empty-upcoming">
                      <Icon name="Calendar" size={48} className="mx-auto text-gray-400 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('No upcoming assignments')}</h3>
                      <p className="text-gray-600">{t('You have no upcoming match assignments.')}</p>
                    </div>
                  )
                ) : pastAssignments.length > 0 ? (
                  <>
                    {pastPaged.slice.map((a) => renderAssignmentCard(a, true))}
                    {pastAssignments.length > pageSize && (
                      <div className="pt-2">
                        <Pagination
                          currentPage={pastPage}
                          totalPages={pastPaged.totalPages}
                          onPageChange={setPastPage}
                          pageSize={pageSize}
                          onPageSizeChange={(s) => { setPageSize(s); setUpcomingPage(1); setPastPage(1); }}
                          totalCount={pastAssignments.length}
                          variant="full"
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-card border border-border rounded-lg p-12 text-center" data-testid="empty-past">
                    <Icon name="CheckCircle" size={48} className="mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('No past assignments')}</h3>
                    <p className="text-gray-600">{t('You have no completed match assignments.')}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {hubTab === 'schedule' && (
        <div data-testid="hub-content-schedule">
          <MyScheduleTab />
        </div>
      )}

      {hubTab === 'workpoints' && (
        <div data-testid="hub-content-workpoints">
          <WorkPointsTab />
        </div>
      )}

      <DeclineAssignmentModal
        isOpen={showDeclineModal}
        onClose={() => setShowDeclineModal(false)}
        onDecline={handleDecline}
        matchId={declineMatchId}
        matchDescription={declineMatchDescription}
      />
      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

export default MyAssignments;
